import { useEffect, useRef, useState } from "react";
import { useCustomController } from "components/Reusable/Form/useCustomController.js";
import { useContent } from "contexts/contentContext/contentContext.js";
import { useUser } from "contexts/userContext/userContext.js";
import { useUploader } from "contexts/uploaderContext/uploaderContext.js";
import apiRequest from "utils/ApiRequest/apiRequest.js";
import Tooltip from "components/Elements/Tooltip/Tooltip.js";
import { useModal } from "contexts/modalContext/modalContext.js";
import EmailUserExistModal from "../Modals/EmailUserExistModal.js";
import VerificationContainer from "../Modals/VerificationContainer.js";
import useAvoidFirstMount from "hooks/useMountedState.js";
import { EMAIL_VALIDATOR } from "helpers/validators.js";
import useMoveableLabel from "./useMoveableLabel.js";
import { getLocalStorage } from "utils/globalLocalStorage.js";
import { CustomErrorSimpleModal } from "hooks/useCustomLoginErrorHandler.js";
import useOnClickOutside from "hooks/useOnClickOutside.js";

export const useCheckSenderEmail = ({ viaLink }) => {
  const { isLoggedIn, getUserData } = useUser();
  const { setModal } = useModal();
  const [subUserErrors, setSubUserErrors] = useState();

  const {
    getVerifiedEmail,
    setVerifiedEmail,
    setMailVerifying,
    getSpamProtection,
    setMailVerifyingProcess,
    getMailVerifyingProcess,
    getMailId,
  } = useUploader();

  // checking spam settings from DB
  const getNoSpamProtection = () => {
    const isLink = getLocalStorage("sendViaLink") === "link" || viaLink;
    if (!getSpamProtection().link && !getSpamProtection().mail) {
      return true;
    }
    if (isLink && !getSpamProtection().link) {
      return true;
    }
    if (!isLink && !getSpamProtection().mail) {
      return true;
    }
  };

  // opening confirmation modal
  const handleOpenConfirmEmailModal = async ({ email, isConfirmEmail }) => {
    if (!getMailId()) return;

    await apiRequest(
      "UserVerifiedCode",
      "RequiredVerification",
      {
        sender_email_address: email,
        mailId: getMailId(),
      },
      "POST"
    ).then((res) => {
      if (res.status === "success") {
        setModal({
          modal: EmailUserExistModal,
          container: VerificationContainer,
          props: {
            email,
            setMailVerifying,
            isConfirmEmail,
            setMailVerifyingProcess,
            getMailVerifyingProcess,
            mailId: getMailId(),
            callback: () => {
              setVerifiedEmail(email);
            },
          },
        });
      } else {
        const errorData = JSON.parse(res.data || "{}");
        if (errorData.errorMessage === "sub-user-must-join-organization") {
          setSubUserErrors(errorData);
        }
      }
    });
  };

  // asking to confirm the email if user did login but didnt confirm the email
  const handleConfirmLoggedInUser = () => {
    if (
      getNoSpamProtection() ||
      getVerifiedEmail() === getUserData().UserName
    ) {
      return true;
    }
    if (!getUserData().IsConfirm) {
      handleOpenConfirmEmailModal({
        email: getUserData().UserName,
        isConfirmEmail: true,
      });
      return false;
    } else {
      setVerifiedEmail(getUserData().UserName);
      return true;
    }
  };

  const handleCheckIfEmailExist = async ({ email }) => {
    // do check only in case the email is valid
    if (!EMAIL_VALIDATOR.test(email)) return true;
    // if no spam protection is disabled or email is verified we return true and continue the process
    if (getNoSpamProtection() || getVerifiedEmail() === email) {
      return true;
    }
    // if the user is logged in we check if the email is exist and confirmed
    if (!isLoggedIn()) {
      handleOpenConfirmEmailModal({ email: email, isConfirmEmail: false });
      return false;
    }
  };

  return {
    handleCheckIfEmailExist,
    handleConfirmLoggedInUser,
    subUserErrors,
    setSubUserErrors,
  };
};

const UserEmailInput = (props) => {
  const { fieldControls, hasError } = useCustomController(props);
  const { getTextByKey } = useContent();
  const { getUserData, isLoggedIn } = useUser();
  const containerRef = useRef();
  const subuserErrorRef = useRef();
  // holding approved mail value
  const { getUploading, setMailVerifyingProcess } = useUploader();
  const [value, setValue] = useState(fieldControls.value || "");

  // request to check if the email exist in the system
  const {
    handleCheckIfEmailExist,
    handleConfirmLoggedInUser,
    subUserErrors,
    setSubUserErrors,
  } = useCheckSenderEmail({ viaLink: props.viaLink });
  useOnClickOutside(subuserErrorRef, () => setSubUserErrors(undefined));

  useEffect(() => {
    if (isLoggedIn()) {
      setTimeout(() => {
        handleConfirmLoggedInUser();
      }, 1000);
    }
  }, [isLoggedIn()]);

  // avoiding first mount changing value in case the user is logged in or logged out
  useAvoidFirstMount({
    callback: () => {
      if (isLoggedIn()) {
        setValue(getUserData().UserName);
      } else {
        setValue("");
      }
    },
    dependencies: [isLoggedIn()],
  });

  useEffect(() => {
    // changing value direrct to body
    setMailVerifyingProcess({ time: null, sessionId: null });
    if (props.externalProps?.setBody) {
      props.externalProps.setBody((prev) => ({
        ...prev,
        SendOwnerEmail: value,
      }));
    }
    fieldControls.onChange(value);
  }, [value]);

  const blurEventHandle = () => {
    handleCheckIfEmailExist({ email: value });
  };

  const { labelClassName } = useMoveableLabel({
    value,
    label: props.item.label,
  });

  const containerPos = containerRef.current?.getBoundingClientRect?.();

  return (
    <label
      ref={containerRef}
      className={`user-email-input-label border-bottom-only${
        hasError ? " invalid-input" : ""
      }`}
    >
      {subUserErrors && Object.keys(subUserErrors).some((key) => key) ? (
        <div
          ref={subuserErrorRef}
          className="user-email-subuser-error-container"
          style={{
            top: containerPos?.bottom,
            left: containerPos?.left,
          }}
        >
          <CustomErrorSimpleModal
            errObj={subUserErrors}
            className="user-email-subuser-error"
          />
        </div>
      ) : null}

      <div className={labelClassName || `input-label`}>
        {getTextByKey("your-email")}
      </div>
      {isLoggedIn() ? (
        <Tooltip
          tooltipText={getTextByKey("cant-change-mail-logged-in-tooltip")}
          start
        >
          <input
            className={`input-field`}
            name="SendOwnerEmail"
            placeholder=""
            autoComplete="SendOwnerEmail"
            disabled={isLoggedIn() || getUploading()}
            onChange={(e) => setValue(e.target.value)}
            value={value}
            id="SendOwnerEmail-id"
          />
        </Tooltip>
      ) : (
        <input
          className={`input-field`}
          name="SendOwnerEmail"
          placeholder=""
          autoComplete="SendOwnerEmail"
          disabled={isLoggedIn() || getUploading()}
          onChange={(e) => setValue(e.target.value)}
          value={value}
          id="SendOwnerEmail-id"
          onBlur={blurEventHandle}
        />
      )}
    </label>
  );
};

export default UserEmailInput;
